import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { PaymentMethodTypes } from '@/feature/deposit/const';
import { useTrustlyKycStore } from '@/feature/kyc/hooks/use-trustly-kyc-store';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useGetAuthUserTrustlyInfo } from '@/hooks/use-user-trustly-information';
import { navigateToKyc } from '@/navigation/navigation';
import { WithdrawStackNavigationProp } from '@/navigation/withdraw/types';

import { getLatestTrustlyMethod } from '../utils/utils';
import { useWithdrawalMethods } from './use-withdrawal-methods';

type TrustlyWithdrawalSalvageProps = { attemptedOnlyNonTrustly: boolean };
type TrustlyWithdrawalSalvageResponse =
    | { isApplicable: false }
    | { isApplicable: true; label: string; onPress: () => void };

export const useTrustlyWithdrawalSalvage = ({
    attemptedOnlyNonTrustly,
}: TrustlyWithdrawalSalvageProps): TrustlyWithdrawalSalvageResponse => {
    const { t } = useTranslation(['account']);
    const navigation = useNavigation<WithdrawStackNavigationProp>();
    const { data: trustlyInfo } = useGetAuthUserTrustlyInfo();
    const { data: methodsResponse } = useWithdrawalMethods();
    const methods = methodsResponse?.data ?? [];

    const globalSettingsFlags = useJurisdictionStore.getState().jurisdictionSettings?.globalSettings?.featureFlags;
    const isNonWithdrawableCheckEnabled = globalSettingsFlags?.enable_non_withdrawable_check?.enabled ?? false;

    if (isNonWithdrawableCheckEnabled) return { isApplicable: false };

    if (!attemptedOnlyNonTrustly) return { isApplicable: false };

    const isVerified = trustlyInfo?.TRUSTLY_VERIFIED === true;
    const latestTrustly = isVerified ? getLatestTrustlyMethod(methods) : undefined;
    const verifiedHasTrustlyMethod = !!latestTrustly && latestTrustly.type === PaymentMethodTypes.Trustly;

    if (isVerified) {
        if (!verifiedHasTrustlyMethod) return { isApplicable: false };
        return {
            isApplicable: true,
            label: t('account:try_with_trustly'),
            onPress: () =>
                navigation.navigate('WithdrawForm', {
                    selectedMethod: latestTrustly,
                    methods,
                }),
        };
    }

    return {
        isApplicable: true,
        label: t('account:try_by_linking_account'),
        onPress: () => {
            useTrustlyKycStore.getState().setShouldProceedToWithdrawal(true);
            navigateToKyc(navigation, { screen: 'TrustlyId' });
        },
    };
};
